import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { InfoPopoverComponent } from '../info-popover/info-popover.component';
import { VerifierService } from '../../services/verifier.service';
import {
  TokenCheck,
  TokenConfiguration,
} from '../../services/utilities.service';

type VerificationResult = {
  success: boolean;
  response: ErrorResponse;
};

type ErrorResponse = {
  messages: string[];
  structure?: string[];
  missing?: string[];
  invalid?: string[];
  groups?: string[];
};

type ComponentVerificationResult = {
  success: boolean;
  errorMessages: string[];
};

@Component({
  selector: 'app-figma-wizard',
  imports: [CommonModule, ReactiveFormsModule, InfoPopoverComponent],
  templateUrl: './figma-wizard.component.html',
  styles: ``,
})
export class FigmaWizardComponent {
  public currentStep = signal(0);
  public maxSteps = 3;
  public wizardForm: FormGroup;
  public steps = [
    {
      title: 'Figma Credentials',
      description:
        'Provide the required Figma credentials to allow access to your designs',
    },
    {
      title: 'Token Configuration',
      description:
        'Specify your foundation and color token node IDs containing the needed token values',
    },
    {
      title: 'Component Validation',
      description:
        'Validate your components against the defined token configuration',
    },
  ];

  private verifierService = inject(VerifierService);

  public isVerifying = signal(false);
  public nodesVerificationResult = signal<VerificationResult | null>(null);
  public figmaAccessVerificationResult = signal<VerificationResult | null>(
    null
  );
  public componentValidations = signal<{
    isValidating: boolean;
    result?: ComponentVerificationResult;
  }>({ isValidating: false, result: undefined });
  public tokenConfiguration = signal<TokenConfiguration | null>(null);

  constructor(private fb: FormBuilder) {
    this.wizardForm = this.fb.group({
      credentials: this.fb.group({
        figmaAccessToken: [
          '',
          [Validators.required],
        ],
        figmaProjectId: ['', [Validators.required]],
      }),
      tokens: this.fb.group({
        foundationTokenNodeId: ['', [Validators.required]],
        colorTokenNodeId: ['', [Validators.required]],
      }),
      componentNode: this.fb.group({
        nodeId: this.fb.control('', [Validators.required]),
      }),
    });
  }

  public nextStep(): void {
    if (this.canProceedToNextStep() && this.currentStep() < this.maxSteps - 1) {
      this.currentStep.set(this.currentStep() + 1);
    }
  }

  public previousStep(): void {
    if (this.currentStep() > 0) {
      this.currentStep.set(this.currentStep() - 1);
    }
  }

  public canProceedToNextStep(): boolean {
    switch (this.currentStep()) {
      case 0:
        return (
          (this.wizardForm.get('credentials')?.valid ?? false) &&
          (this.figmaAccessVerificationResult()?.success ?? false)
        );
      case 1:
        return (
          (this.wizardForm.get('tokens')?.valid ?? false) &&
          (this.nodesVerificationResult()?.success ?? false)
        );
      default:
        return false;
    }
  }

  public canVerifyFigmaAccess(): boolean {
    return this.wizardForm.get('credentials')?.valid ?? false;
  }

  public verifyFigmaAccess(): void {
    if (!this.canVerifyFigmaAccess()) return;

    const formData = this.getFormData();
    this.isVerifying.set(true);
    this.figmaAccessVerificationResult.set(null);

    this.verifierService
      .figmaAccess(
        formData.credentials.figmaAccessToken,
        formData.credentials.figmaProjectId
      )
      .subscribe({
        next: (data) => {
          this.isVerifying.set(false);
          this.figmaAccessVerificationResult.set({
            success: true,
            response: {
              messages: ['Figma access confirmed!'],
            },
          });
        },
        error: () => {
          this.isVerifying.set(false);
          this.figmaAccessVerificationResult.set({
            success: false,
            response: {
              messages: [
                'Could not connect to the provided project with the provided access token. Please check your project ID and ensure the provided token is granted access to the project.',
              ],
            },
          });
        },
      });
  }

  public canVerifyTokens(): boolean {
    return this.wizardForm.get('tokens')?.valid ?? false;
  }

  public verifyTokenNodes(): void {
    if (!this.canVerifyTokens()) return;

    this.isVerifying.set(true);
    this.nodesVerificationResult.set(null);

    const formData = this.getFormData();

    this.verifierService
      .checkTokens(
        formData.credentials.figmaAccessToken,
        formData.credentials.figmaProjectId,
        formData.tokens.foundationTokenNodeId,
        formData.tokens.colorTokenNodeId
      )
      .subscribe({
        next: (result: TokenCheck) => {
          this.isVerifying.set(false);
          const success = result?.tokenConfiguration !== null;
          if (success) {
            this.tokenConfiguration.set(result.tokenConfiguration);
            console.log(this.tokenConfiguration());
          }
          this.nodesVerificationResult.set({
            success,
            response: success
              ? {
                  messages: [
                    'Token structure verified successfully! Foundation and color tokens are properly configured.',
                  ],
                }
              : result?.errorMessages !== null
              ? { messages: result?.errorMessages }
              : { messages: [], ...result?.errors },
          });
        },
        error: (err) => {
          this.nodesVerificationResult.set({
            success: false,
            response: {
              messages: ['Something went wrong. Please try again.'],
            },
          });
          this.isVerifying.set(false);
        },
      });
  }

  public validateComponent() {
    const control = this.wizardForm.get('componentNode');
    if (!control || control?.invalid || this.tokenConfiguration() == null) {
      return;
    }

    const formData = this.getFormData();
    const validations = this.componentValidations();
    validations.isValidating = true;
    this.componentValidations.set(validations);

    this.verifierService
      .validateComponent(
        formData.credentials.figmaAccessToken,
        formData.credentials.figmaProjectId,
        formData.componentNodeIds.nodeId,
        this.tokenConfiguration() as TokenConfiguration
      )
      .subscribe({
        next: (errors: string[]) => {
          let success = true;
          let errorMessages: string[] = [];
          if (errors != null && errors.length > 0) {
            success = false;
            errorMessages = errors;
          }

          this.componentValidations.set({
            isValidating: false,
            result: {
              success,
              errorMessages,
            },
          });
        },
        error: (err) => {
          this.componentValidations.set({
            isValidating: false,
            result: {
              success: false,
              errorMessages: ['Something went wrong. Please try again.'],
            },
          });
        },
      });
  }

  public isFieldInvalid(fieldPath: string): boolean {
    const field = this.wizardForm.get(fieldPath);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  private getFormData() {
    return {
      credentials: this.wizardForm.get('credentials')?.value,
      tokens: this.wizardForm.get('tokens')?.value,
      componentNodeIds: this.wizardForm.get('componentNode')?.value,
    };
  }
}
